import 'swiper/css'

import { useCalendarDays, useUncontrolled } from '@byecode/ui'
import { CALENDAR_MODE } from '@lighthouse/core'
import { addMonths, startOfDay } from 'date-fns'
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useAsyncRetry } from 'react-use'
import styled from 'styled-components'
import type { SwiperRef } from 'swiper/react'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useTimezoneOffset } from '../../../../../hooks/useTimezoneOffset'
import { fromShanghaiWall, isBeijingTimeZone, toShanghaiWall } from '../../../../../utils/shanghaiTime'
import { CalendarHeader } from './CalendarHeader'
import { DayCalendar } from './DayCalendar'
import type { CalendarProps } from './types'

const SCxContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: auto hidden;

    .swiper {
        width: 100%;
        height: 100%;
    }
    .swiper-wrapper {
        cursor: auto !important;

        @media (any-hover: hover) {
            cursor: pointer !important;
        }
    }
`

export const Calendar = forwardRef<HTMLDivElement, CalendarProps>((props, ref) => {
    const {
        mode,
        weekShowCount,
        // control props
        disabled,
        date: propDate,
        defaultDate,
        minDate,
        maxDate,
        panelDate,
        defaultPanelDate,
        onChange,
        onFetchDateDescribeMap,
        onFetchEnableSelectedDateList,
        styles,
        timeZone,
        ...rest
    } = props

    const isWeekMode = mode === CALENDAR_MODE.week
    const useBeijing = isBeijingTimeZone(timeZone)
    const tzOffset = useTimezoneOffset()
    const nowInCalendar = () => (useBeijing ? toShanghaiWall(Date.now()) : new Date())

    const [date, setDate] = useUncontrolled({
        value: propDate,
        defaultValue: defaultDate,
        // finalValue: new Date(),
        onChange
    })

    const [panelViewDate, setPanelViewDate] = useUncontrolled({
        value: panelDate,
        defaultValue: defaultPanelDate,
        finalValue: nowInCalendar()
    })

    const [dateDescribeMap, setDateDescribeMap] = useState<Record<number, string>>({})
    const [customEnableDateList, setCustomEnableDateList] = useState<number[] | null>(null)
    const [dayTick, setDayTick] = useState(0)

    const swiperRef = useRef<SwiperRef>(null)

    useEffect(() => {
        if (!isWeekMode) {
            return
        }

        let timer: ReturnType<typeof setTimeout> | undefined

        const schedule = () => {
            const today = startOfDay(useBeijing ? toShanghaiWall(Date.now()) : new Date())
            const nextDay = new Date(today)
            nextDay.setDate(nextDay.getDate() + 1)
            const nextBoundary = useBeijing ? fromShanghaiWall(nextDay) : nextDay.getTime()
            timer = setTimeout(() => {
                setDayTick(tick => tick + 1)
            }, Math.max(nextBoundary - Date.now(), 1000))
        }

        schedule()

        return () => {
            if (timer) {
                clearTimeout(timer)
            }
        }
    }, [isWeekMode, tzOffset, useBeijing])

    const weekBaseDate = useMemo(
        () => startOfDay(useBeijing ? toShanghaiWall(Date.now()) : new Date()),
        [dayTick, tzOffset, useBeijing]
    )

    useEffect(() => {
        if (isWeekMode || date) {
            return
        }
        setPanelViewDate(startOfDay(useBeijing ? toShanghaiWall(Date.now()) : new Date()))
    }, [date, isWeekMode, setPanelViewDate, tzOffset, useBeijing])

    const rows = useCalendarDays({
        mode,
        calendarDate: isWeekMode ? weekBaseDate : panelViewDate,
        dayRowsCount: isWeekMode ? 1 : 6,
        firstDayOfWeek: 0
    })

    useEffect(() => {
        if (isWeekMode) {
            return
        }
        if (date) {
            setPanelViewDate(date)
        }
    }, [date, isWeekMode, setPanelViewDate])

    useAsyncRetry(async () => {
        const floatDayList = rows.flat()
        const firstDay = floatDayList[0]
        const lastDay = floatDayList[floatDayList.length - 1]
        if (onFetchDateDescribeMap) {
            const map = await onFetchDateDescribeMap(firstDay, lastDay)
            setDateDescribeMap(map || {})
        }
        if (onFetchEnableSelectedDateList) {
            const list = await onFetchEnableSelectedDateList(firstDay, lastDay)
            if (list) {
                setCustomEnableDateList([...new Set(list)])
            } else {
                setCustomEnableDateList(null)
            }
        }
    }, [rows, onFetchDateDescribeMap, onFetchEnableSelectedDateList])

    return (
        <SCxContainer ref={ref} {...rest}>
            <CalendarHeader
                mode={mode}
                weekShowCount={weekShowCount}
                date={isWeekMode ? weekBaseDate : panelViewDate}
                onPrevYear={d => {
                    // swiperRef?.current?.swiper.slidePrev()
                    setPanelViewDate(addMonths(panelViewDate, -12))
                }}
                onPrevMonth={d => {
                    // setPanelViewDate(addMonths(panelViewDate, -1))
                    swiperRef?.current?.swiper.slidePrev()
                }}
                onNextYear={d => {
                    setPanelViewDate(addMonths(panelViewDate, 12))
                    // swiperRef?.current?.swiper.slideNext()
                }}
                onNextMonth={d => {
                    // setPanelViewDate(addMonths(panelViewDate, 1))
                    swiperRef?.current?.swiper.slideNext()
                }}
                styles={styles}
            />

            {isWeekMode ? (
                <DayCalendar
                    mode={mode}
                    weekShowCount={weekShowCount}
                    disabled={disabled}
                    date={date}
                    onChange={setDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    panelViewDate={isWeekMode ? weekBaseDate : panelViewDate}
                    dateDescribeMap={dateDescribeMap}
                    customEnableDateList={customEnableDateList}
                    rows={rows}
                    styles={styles}
                />
            ) : (
                <Swiper
                    grabCursor
                    effect="creative"
                    loop
                    ref={swiperRef}
                    onSlidePrevTransitionStart={() => {
                        setPanelViewDate(addMonths(panelViewDate, -1))
                    }}
                    onSlideNextTransitionStart={() => {
                        setPanelViewDate(addMonths(panelViewDate, 1))
                    }}
                >
                    <SwiperSlide>
                        <DayCalendar
                            disabled={disabled}
                            date={date}
                            onChange={setDate}
                            minDate={minDate}
                            maxDate={maxDate}
                            panelViewDate={panelViewDate}
                            dateDescribeMap={dateDescribeMap}
                            customEnableDateList={customEnableDateList}
                            rows={rows}
                            styles={styles}
                        />
                    </SwiperSlide>
                    <SwiperSlide>
                        <DayCalendar
                            disabled={disabled}
                            date={date}
                            onChange={setDate}
                            minDate={minDate}
                            maxDate={maxDate}
                            panelViewDate={panelViewDate}
                            dateDescribeMap={dateDescribeMap}
                            customEnableDateList={customEnableDateList}
                            rows={rows}
                            styles={styles}
                        />
                    </SwiperSlide>
                </Swiper>
            )}
        </SCxContainer>
    )
})
